import { InputNumberLocalized } from '@components'

import { Button, ButtonsGroup, Modal, ModalProps, withModalConditionalRender } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useDebouncedCallback } from '../../../../../../../../../../../../../../hooks/useDebouncedCallback'
import { useInvoiceEditorForm } from '../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useQuoteNumber } from '../../../../contexts/quoteNumberContext'

export const QuoteNumberModalComponent = (modalProps: ModalProps): ReactElement => {
  const { t } = useTranslation()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { closeQuoteNumberModal, saveQuoteNumber } = useQuoteNumber()
  const { FormItem, trigger, watch } = invoiceEditorForm

  const { invoiceNumber } = watch()

  // Ensure stable function: InputNumberLocalized triggers onChange multiple times
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTrackInvoiceEditor = useCallback(trackInvoiceEditor, [])

  const handleDebounceNumberLocalizedInputChange = useDebouncedCallback(() =>
    stableTrackInvoiceEditor('Form - Invoice number modal - Invoice number typed'),
  )

  const handleCancelButtonClick = useCallback(() => {
    closeQuoteNumberModal()
  }, [closeQuoteNumberModal])

  const handleSaveButtonClick = useCallback(() => {
    saveQuoteNumber()
  }, [saveQuoteNumber])

  return (
    <Modal size="s" {...modalProps}>
      <Modal.Header title={t('external_invoices.editor.quote_number_modal.title')} />
      <Modal.Body>
        <FormItem
          name="invoiceNumber"
          label={t('external_invoices.editor.quote_number_modal.number.label')}
          render={({ field: { value, onChange, ...props } }) => {
            return (
              <InputNumberLocalized
                {...props}
                alignment="left"
                onChange={(event) => {
                  onChange(event)
                  trigger('invoiceNumber')
                  handleDebounceNumberLocalizedInputChange()
                }}
                step={1}
                size="sm"
                type="number"
                value={invoiceNumber}
                withFormatting={false}
              />
            )
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button variant="text" onClick={handleCancelButtonClick}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSaveButtonClick}>{t('save')}</Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}

export const QuoteNumberModal = withModalConditionalRender(QuoteNumberModalComponent)
